import { AfterViewChecked, AfterViewInit, Component, ElementRef, NgZone, OnDestroy, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { TestLogModalService, TestLogEntry, TestLogEntryAttachment, TestLogModalContext } from 'src/app/services/test-log-modal.service';
import { CommonService } from 'src/app/services/common.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';

@Component({
  selector: 'test-log-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './test-log-modal.component.html',
  styleUrl: './test-log-modal.component.scss'
})
export class TestLogModalComponent implements AfterViewInit, AfterViewChecked, OnDestroy {
  @ViewChild('content') private content!: TemplateRef<unknown>;
  @ViewChildren('descriptionText') private descriptionTexts!: QueryList<ElementRef<HTMLElement>>;

  ctx: TestLogModalContext | null = null;

  /** Indici (relativi alla pagina corrente) delle descrizioni espanse oltre il clamp. */
  private expandedDescriptions = new Set<number>();

  private overflowingDescriptions = new Set<number>();

  private measurementScheduled = false;

  readonly pageSize = 8;
  currentPage = 1;

  private modalRef?: NgbModalRef;
  private readonly subscription = new Subscription();

  constructor(
    private testLogModalService: TestLogModalService,
    private commonService: CommonService,
    private alertNotificationService: AlertNotificationService,
    private modalService: NgbModal,
    private ngZone: NgZone
  ) { }

  ngAfterViewInit(): void {
    this.subscription.add(
      this.testLogModalService.state$.subscribe(state => {
        if (state.isOpen && state.context) {
          this.ctx = state.context;
          this.openModal();
        } else {
          this.closeModal();
        }
      })
    );
  }

  ngAfterViewChecked(): void {
    this.scheduleOverflowMeasurement();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.closeModal();
  }

  private openModal(): void {
    if (this.modalRef) {
      return;
    }

    this.modalRef = this.modalService.open(this.content, {
      size: 'xl',
      centered: true,
      windowClass: 'test-log-modal'
    });

    // Chiusura con ESC o click sul backdrop.
    this.modalRef.result.catch(() => {
      this.modalRef = undefined;
      if (this.ctx) {
        this.close();
      }
    });
  }

  private closeModal(): void {
    this.ctx = null;
    const ref = this.modalRef;
    this.modalRef = undefined;
    ref?.dismiss();
  }

  getPagedLogs(logs: any[]): any[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return logs.slice(start, start + this.pageSize);
  }

  totalPages(logs: any[]): number {
    return Math.ceil(logs.length / this.pageSize);
  }

  pageRange(logs: any[]): number[] {
    return Array.from({ length: this.totalPages(logs) }, (_, i) => i + 1);
  }

  goToPage(page: number, logs: any[]): void {
    if (page < 1 || page > this.totalPages(logs)) return;
    this.currentPage = page;
    this.expandedDescriptions.clear();
    this.overflowingDescriptions.clear();
  }

  toggleDescription(index: number): void {
    if (this.expandedDescriptions.has(index)) {
      this.expandedDescriptions.delete(index);
    } else {
      this.expandedDescriptions.add(index);
    }
  }

  isDescriptionExpanded(index: number): boolean {
    return this.expandedDescriptions.has(index);
  }

  isDescriptionOverflowing(index: number): boolean {
    return this.overflowingDescriptions.has(index);
  }

  private scheduleOverflowMeasurement(): void {
    if (this.measurementScheduled || !this.descriptionTexts) return;

    this.measurementScheduled = true;
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.measurementScheduled = false;

        const overflowing = this.measureOverflowingDescriptions();
        if (this.sameIndexes(overflowing, this.overflowingDescriptions)) return;

        this.ngZone.run(() => {
          this.overflowingDescriptions = overflowing;
        });
      });
    });
  }

  private measureOverflowingDescriptions(): Set<number> {
    const overflowing = new Set<number>();

    this.descriptionTexts.forEach(ref => {
      const element = ref.nativeElement;
      const index = Number(element.dataset['logIndex']);
      // Quando la descrizione è espansa il clamp non è attivo e non misurerebbe più alcun
      // overflow: manteniamo il toggle per permettere il "Show less".
      if (this.expandedDescriptions.has(index) || element.scrollHeight > element.clientHeight + 1) {
        overflowing.add(index);
      }
    });

    return overflowing;
  }

  private sameIndexes(a: Set<number>, b: Set<number>): boolean {
    return a.size === b.size && [...a].every(index => b.has(index));
  }

  close(): void {
    this.expandedDescriptions.clear();
    this.overflowingDescriptions.clear();
    this.currentPage = 1;
    this.testLogModalService.close();
  }

  getLogStatusLabel(status: string): string {
    switch (status) {
      case 'PASSED': return 'Pass';
      case 'FAILED': return 'Fail';
      case 'SKIPPED': return 'N/A';
      default: return status;
    }
  }

  getLogStatusClass(status: string): string {
    switch (status) {
      case 'PASSED': return 'badge-passed';
      case 'FAILED': return 'badge-failed';
      case 'SKIPPED': return 'badge-na';
      default: return '';
    }
  }

  downloadAttachment(log: TestLogEntry, attachment: TestLogEntryAttachment): void {
    if (!log.projectIssueId || !attachment.fileName) return;

    this.commonService.getAttachments(
      'PROJECT_ISSUE',
      'EVIDENCE',
      log.projectIssueId,
      attachment.fileName,
      new HttpParams()
    ).subscribe({
      next: (response: any) => {
        window.open(response.data.attachment.publicUrl, '_blank');
      },
      error: (error: any) => {
        this.alertNotificationService.error('Error downloading file');
        console.error('Error:', error);
      }
    });
  }
}
